import styled from 'styled-components';
import { theme } from '../theme';

// Profile View Container
export const ProfileViewContainer = styled.div`
  width: 100%;
  margin: 0;
  padding: 0;
  background: var(--background);
  min-height: 100vh;
  color: ${theme.colors.text.primary};
`;

// Profile View Header
export const ProfileViewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.xl};
  padding-bottom: ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.border.light};

  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: ${theme.spacing.md};
    align-items: flex-start;
  }
`;

export const ProfileViewTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0;
  text-shadow: none;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.5rem;
  }
`;

export const ProfileViewActions = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
`;

// Action Buttons
export const ActionButton = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};

  &:hover {
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const EditButton = styled(ActionButton)`
  background: ${theme.colors.primary};
  color: white;

  &:hover {
    background: ${theme.colors.primary};
    opacity: 0.9;
    box-shadow: 0 4px 12px rgba(255, 122, 48, 0.3);
  }
`;

export const ShareButton = styled(ActionButton)`
  background: ${theme.colors.secondary};
  color: white;

  &:hover {
    background: ${theme.colors.secondary};
    opacity: 0.9;
    box-shadow: 0 4px 12px rgba(108, 92, 231, 0.3);
  }
`;

// Profile View Content
export const ProfileViewContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`;

// Profile Card
export const ProfileCard = styled.div`
  background: var(--bg-card);
  border-radius: 0;
  padding: ${theme.spacing.xl};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border: none;
  border-top: 1px solid ${theme.colors.border.light};
  border-bottom: 1px solid ${theme.colors.border.light};
  color: var(--text-primary);
  width: 100%;
  margin: 0;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.lg} ${theme.spacing.md};
    border-radius: 0;
    /* Better mobile spacing */
    padding-bottom: ${theme.spacing.xl};
  }
  
  @media (max-width: 400px) {
    padding: ${theme.spacing.md} ${theme.spacing.sm};
  }
`;

// Profile Header
export const ProfileHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.xl};
  padding-top: ${theme.spacing.md};
`;

export const ProfilePhoto = styled.div<{ $hasStories?: boolean }>`
  width: 120px;
  height: 120px;
  border-radius: ${theme.borderRadius.full};
  overflow: visible;
  flex-shrink: 0;
  cursor: ${props => props.$hasStories ? 'pointer' : 'default'};
  border: 4px solid ${props => props.$hasStories ? theme.colors.primary : theme.colors.primary};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin: 0 auto;
  position: relative;
  
  ${props => props.$hasStories ? `
    &::before {
      content: '';
      position: absolute;
      top: -6px;
      left: -6px;
      right: -6px;
      bottom: -6px;
      border-radius: ${theme.borderRadius.full};
      background: linear-gradient(45deg, #ff6b9d, #c06c84, #ffd700, #ffa500, #ff6b9d);
      background-size: 400% 400%;
      z-index: 0;
      animation: storyRing 3s ease infinite;
      pointer-events: none;
    }
    
    @keyframes storyRing {
      0% {
        background-position: 0% 50%;
        opacity: 1;
      }
      50% {
        background-position: 100% 50%;
        opacity: 0.8;
      }
      100% {
        background-position: 0% 50%;
        opacity: 1;
      }
    }
  ` : ''}

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: ${theme.borderRadius.full};
    position: relative;
    z-index: 1;
  }

  .placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${theme.colors.background.secondary};
    font-size: 3rem;
    color: var(--muted-foreground);
    border-radius: ${theme.borderRadius.full};
    position: relative;
    z-index: 1;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 100px;
    height: 100px;
  }
`;

export const StoryPlusButton = styled.button`
  position: absolute;
  bottom: -5px;
  right: -5px;
  width: 36px;
  height: 36px;
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.primary};
  border: 3px solid white;
  color: white;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 10;
  transition: all 0.2s ease;
  line-height: 1;
  padding: 0;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 32px;
    height: 32px;
    font-size: 1rem;
    bottom: -4px;
    right: -4px;
  }
`;

export const ProfileInfo = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.sm};

  .occupation,
  .education {
    color: var(--text-secondary);
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${theme.spacing.xs};
  }
  
  .bio {
    margin-top: ${theme.spacing.md};
    padding-top: ${theme.spacing.md};
    border-top: 1px solid ${theme.colors.border.light};
    width: 100%;
    max-width: 600px;
    
    p {
      color: ${theme.colors.text.secondary};
      font-size: 0.95rem;
      line-height: 1.6;
      margin: 0;
      text-align: center;
    }
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: ${theme.spacing.xs};
    
    .occupation,
    .education {
      font-size: 0.85rem;
    }
    
    .bio {
      margin-top: ${theme.spacing.sm};
      padding-top: ${theme.spacing.sm};
      
      p {
        font-size: 0.9rem;
        line-height: 1.7;
      }
    }
  }
`;

export const ProfileNameContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  flex-wrap: wrap;
  width: 100%;
  margin: ${theme.spacing.xs} 0;
`;

export const ProfileName = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.5rem;
  }
`;

export const PremiumBadge = styled.div`
  color: #FFD700;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.2rem;
  }
`;

export const IdentityContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  flex-wrap: wrap;
  margin: ${theme.spacing.xs} 0 ${theme.spacing.md} 0;
`;

export const ProfileBadgePill = styled.div<{ $type: 'heart-choices' | 'verifier'; $clickable?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  background: ${props => props.$type === 'heart-choices' ? 'linear-gradient(135deg, #FFF0F5 0%, #FFE4E1 100%)' : theme.colors.background.secondary};
  border: 1px solid ${props => props.$type === 'heart-choices' ? '#FFB6C1' : theme.colors.border.light};
  border-radius: ${theme.borderRadius.full};
  font-size: 0.9rem;
  font-weight: 600;
  color: ${props => props.$type === 'heart-choices' ? '#E91E63' : theme.colors.text.primary};
  box-shadow: 0 2px 5px rgba(0,0,0,0.05);

  /* On your own profile the badge is the way back to your results, so it has
     to look like something you can press. On someone else's it is a label. */
  cursor: ${props => (props.$clickable ? 'pointer' : 'default')};
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  ${props => props.$clickable && `
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 10px rgba(0,0,0,0.12);
    }
    &:active { transform: translateY(0); }
    &:focus-visible {
      outline: 2px solid #E91E63;
      outline-offset: 2px;
    }
  `}

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    font-size: 0.85rem;
  }
`;

export const ProfilePersona = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.full};
  border: 1px solid ${theme.colors.border.light};
  width: fit-content;

  .persona-icon {
    font-size: 1.2rem;
  }

  .persona-name {
    font-size: 0.9rem;
    font-weight: 600;
    color: ${theme.colors.text.primary};
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    
    .persona-icon {
      font-size: 1rem;
    }
    
    .persona-name {
      font-size: 0.85rem;
    }
  }
`;

export const ProfileAge = styled.div`
  font-size: 1.1rem;
  color: var(--text-secondary);
  font-weight: 500;
  text-align: center;
`;

export const ProfileLocation = styled.div`
  font-size: 1rem;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.xs};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.9rem;
  }
`;

// Photo Grid
export const ProfilePhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: repeat(3, 1fr);
    gap: ${theme.spacing.sm};
    margin-top: ${theme.spacing.sm};
  }
  
  @media (max-width: 400px) {
    grid-template-columns: repeat(3, 1fr);
    gap: ${theme.spacing.xs};
  }
`;

export const PhotoItem = styled.div<{ $isActive?: boolean }>`
  position: relative;
  aspect-ratio: 1;
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  cursor: pointer;
  border: 2px solid ${({ $isActive }) => $isActive ? theme.colors.primary : 'transparent'};
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  user-select: none;

  &:hover {
    transform: scale(1.05);
    border-color: ${theme.colors.primary};
  }
  
  &:active {
    transform: scale(0.95);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    border-radius: ${theme.borderRadius.sm};
    
    &:hover {
      transform: none; /* Disable hover transform on mobile */
    }
  }
`;

export const VideoIndicator = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  z-index: 2;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 20px;
    height: 20px;
    top: 4px;
    right: 4px;
    
    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

// Profile Badges
export const ProfileBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.xl};
`;

export const BadgeItem = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  background: ${({ $color }) => $color}20;
  border: 1px solid ${({ $color }) => $color}40;
  border-radius: ${theme.borderRadius.full};
  font-size: 0.8rem;
  font-weight: 600;
`;

export const BadgeIcon = styled.span`
  font-size: 1rem;
`;

export const BadgeText = styled.span`
  color: var(--text-primary);
`;

// Profile Bio
export const ProfileBio = styled.div`
  margin-bottom: ${theme.spacing.xl};

  h4 {
    font-size: 1.2rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 ${theme.spacing.md} 0;
  }

  p {
    font-size: 1rem;
    line-height: 1.6;
    color: var(--text-secondary);
    margin: 0;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-bottom: ${theme.spacing.lg};
    
    h4 {
      font-size: 1.1rem;
      margin-bottom: ${theme.spacing.sm};
    }
    
    p {
      font-size: 0.95rem;
      line-height: 1.7;
    }
  }
`;

// Profile Interests
export const ProfileInterests = styled.div`
  margin-bottom: ${theme.spacing.xl};

  h4 {
    font-size: 1.2rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 ${theme.spacing.md} 0;
  }

  .interests-grid {
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing.sm};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-bottom: ${theme.spacing.lg};
    
    h4 {
      font-size: 1.1rem;
      margin-bottom: ${theme.spacing.sm};
    }
    
    .interests-grid {
      gap: ${theme.spacing.xs};
    }
  }
`;

export const InterestTag = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  background: ${theme.colors.primary}15;
  border: 1px solid ${theme.colors.primary}30;
  border-radius: ${theme.borderRadius.full};
  font-size: 0.9rem;
  font-weight: 500;
  -webkit-tap-highlight-color: transparent;
  user-select: none;

  .icon {
    font-size: 1rem;
  }

  .name {
    color: var(--text-primary);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    font-size: 0.85rem;
    
    .icon {
      font-size: 0.95rem;
    }
  }
`;

// Profile Preferences
export const ProfilePreferences = styled.div`
  margin-bottom: ${theme.spacing.xl};

  h4 {
    font-size: 1.2rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 ${theme.spacing.sm} 0;
  }
`;

export const PreferenceItem = styled.div`
  font-size: 0.9rem;
  color: ${theme.colors.text.secondary};
  margin-bottom: ${theme.spacing.xs};

  strong {
    color: var(--text-primary);
    font-weight: 600;
  }
`;

// Profile Stats
export const ProfileStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.xl};
  padding: ${theme.spacing.lg};
  background: ${theme.colors.background.card};
  border-radius: ${theme.borderRadius.md};

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${theme.spacing.sm};
    padding: ${theme.spacing.md};
  }
`;

export const StatItem = styled.div`
  text-align: center;
`;

export const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.xs};
`;

export const StatLabel = styled.div`
  font-size: 0.8rem;
  color: ${theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// Profile Completion
export const ProfileCompletion = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

export const CompletionBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
  margin: ${theme.spacing.sm} 0;

  .completion-fill {
    height: 100%;
    background: linear-gradient(90deg, ${theme.colors.primary}, ${theme.colors.secondary});
    transition: width 0.3s ease;
  }
`;

export const CompletionText = styled.div`
  font-size: 0.9rem;
  color: ${theme.colors.text.secondary};
  text-align: center;
`;

// Profile Actions
export const ProfileActions = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: center;
  margin-top: ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

export const LikeButton = styled(ActionButton)`
  background: #FF6B6B;
  color: white;
  padding: ${theme.spacing.md} ${theme.spacing.lg};

  &:hover {
    background: #FF5252;
    box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
  }
`;

export const MessageButton = styled(ActionButton)`
  background: ${theme.colors.primary};
  color: white;
  padding: ${theme.spacing.md} ${theme.spacing.lg};

  &:hover {
    background: ${theme.colors.primary};
    opacity: 0.9;
    box-shadow: 0 4px 12px rgba(255, 122, 48, 0.3);
  }
`;

export const ViewButton = styled(ActionButton)`
  background: ${theme.colors.secondary};
  color: white;
  padding: ${theme.spacing.md} ${theme.spacing.lg};

  &:hover {
    background: ${theme.colors.secondary};
    opacity: 0.9;
    box-shadow: 0 4px 12px rgba(108, 92, 231, 0.3);
  }
`;

// Edit Profile Button (for own profile) - Small button next to name
export const EditProfileButton = styled.button`
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  transition: all 0.2s ease;
  line-height: 1;

  &:hover {
    background: ${theme.colors.hover.primary};
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.medium};
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.7rem;
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
  }
`;

export const AddToDeckButton = styled(ActionButton)<{ $isAdded: boolean }>`
  background: ${({ $isAdded }) =>
    $isAdded 
      ? "#2ecc71" 
      : `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primary}dd 100%)`};
  color: white;
  padding: ${theme.spacing.md} ${theme.spacing.lg};

  &:hover:not(:disabled) {
    background: ${({ $isAdded }) =>
      $isAdded 
        ? "#27ae60" 
        : `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primary} 100%)`};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  &:disabled {
    opacity: 0.8;
    cursor: not-allowed;
  }
`;

// Compact Action Buttons (positioned under persona)
export const ProfileActionsCompact = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  justify-content: center;
  align-items: center;
  margin: ${theme.spacing.xs} 0 ${theme.spacing.xs} 0;
  width: 100%;

  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: ${theme.spacing.xs};
    margin: ${theme.spacing.xs} 0 ${theme.spacing.xs} 0;
  }
`;

export const MessageButtonCompact = styled.button`
  background: ${theme.colors.background.secondary};
  color: ${theme.colors.text.primary};
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.full};
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  transition: all 0.2s ease;
  flex: 1;
  max-width: 150px;
  justify-content: center;

  &:hover {
    background: ${theme.colors.background.tertiary};
    transform: translateY(-1px);
  }
`;

export const AddToDeckButtonCompact = styled.button<{ $isAdded: boolean }>`
  background: ${({ $isAdded }) =>
    $isAdded 
      ? "rgba(46, 204, 113, 0.1)" 
      : theme.colors.primary};
  color: ${({ $isAdded }) =>
    $isAdded 
      ? "#27ae60" 
      : "white"};
  border: ${({ $isAdded }) =>
    $isAdded 
      ? "1px solid #27ae60" 
      : `1px solid ${theme.colors.primary}`};
  border-radius: ${theme.borderRadius.full};
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  transition: all 0.2s ease;
  flex: 1;
  max-width: 150px;
  justify-content: center;

  &:hover {
    transform: translateY(-1px);
    background: ${({ $isAdded }) =>
      $isAdded 
        ? "rgba(46, 204, 113, 0.2)" 
        : theme.colors.hover.primary};
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

// Video Viewer Components
export const PhotoViewerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${theme.spacing.md};
  backdrop-filter: blur(5px);
`;

export const PhotoViewerContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  max-width: 1024px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const PhotoViewerImage = styled.img`
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  user-select: none;
`;

export const PhotoViewerCloseButton = styled.button`
  position: absolute;
  top: ${theme.spacing.lg};
  right: ${theme.spacing.lg};
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 1010;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;

export const PhotoViewerNavButton = styled.button<{ $position: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.$position}: ${theme.spacing.md};
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    display: none; // Hide arrows on mobile, rely on swipe
  }
`;

export const PhotoViewerIndicator = styled.div`
  position: absolute;
  bottom: ${theme.spacing.lg};
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  background: rgba(0, 0, 0, 0.5);
  padding: 4px 12px;
  border-radius: 12px;
`;
